import React, { PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './Billboard.css';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { useWidthContext } from '../../providers/Width';
import { SideMenuContext } from '../../providers/SideMenuProvider/SideMenuProvider';
import { Icon } from '../../types/shared/Icon';
import axios from 'axios';
import OktaDeferredSignIn from './LoginVariants/OktaDeferredSignIn';
import { getMarketingAttributes } from '../../utils/acquisition-utils';
import { Row, Col, Button, Modal, Section, Header, Title } from '../../importCL';
import BillboardNoSignInBoxVariant from './BillboardVariant/BillboardNoSignInBoxVariant';


const DEFAULT_DESKTOP_IMAGE_WIDTH = 389;
const DEFAULT_DESKTOP_IMAGE_HEIGHT = 245;
const DEFAULT_MOBILE_IMAGE_WIDTH = 300;
const DEFAULT_MOBILE_IMAGE_HEIGHT = 189;

const retrieveSignInBox = (
    width: number,
    signInCtaLabel?: string,
    signInTitle?: string,
    forgotPasswordLink?: string,
    forgotUserNameLink?: string,
    setupAccountLink?: string,
    smsIcon?: Icon,
    emailIcon?: Icon,
    callIcon?: Icon,
    removeHeader?: boolean,
    usingOktaIdentityEngine?: boolean,
): JSX.Element => {
    return (
        <div className={`Billboard__signInBox--okta Billboard__signInBox${width <= 768 ? '--modal' : ''} ${removeHeader ? 'Billboard__signInBox--removedHeader' : ''}`}>
            <OktaDeferredSignIn
                signInCtaLabel={signInCtaLabel}
                signInTitle={signInTitle}
                forgotPasswordLink={forgotPasswordLink}
                forgotUserNameLink={forgotUserNameLink}
                setupAccountLink={setupAccountLink}
                smsIcon={smsIcon}
                emailIcon={emailIcon}
                callIcon={callIcon}
                usingOktaIdentityEngine={usingOktaIdentityEngine}
            />
        </div>
    );
};
interface IMainNavigationItem {
    linkTitle: string;
    link: string;
    linkParam: string;
    linkNewTab: boolean;
}

export type TCreateSideMenu = {
    title: any;
    link?: string;
    linkParams?: string;
    newTab?: boolean;
    next?: TCreateSideMenu | TCreateSideMenu[];
};

export interface IBillboardProps extends MappedComponentProperties {
    author?: boolean;
    usingOktaIdentityEngine?: boolean;

    className?: string;
    signInTitle?: string;

    ctaLinkLabel?: string;
    ctaLink?: string;
    ctaNewTab?: boolean;

    mainText?: string;
    subText?: string;

    bgColor?: string;
    hexColor?: string;

    tertiaryTextDesktop?: string;
    tertiaryTextMobile?: string;

    mainImgPath?: string;
    mainMobileImgPath?: string;
    overwriteMobileStyle?: string;
    usingOverwriteMobileStyle?: boolean;

    mainImgAltText?: string;
    overlayImgPath?: string;
    preLoadDesktopBkgTopImage?: boolean;
    bkgTopImgPath?: string;
    mobileBkgTopImgPath?: string;

    validationErrorCode?: string;
    loginErrorCode?: string;
    genericProcessingErrorCode?: string;
    suspendedAccountFraudErrorCode?: string;
    suspendedAccountGeneralErrorCode?: string;
    failedDeviceCheckErrorCode?: string;

    navBarLinks?: IMainNavigationItem[];
    showHamburgerButton?: boolean;
    hideCtaBtnDesktop?: boolean;

    headerCtaLabel?: string;
    headerCtaLink?: string;
    headerCtaModal?: boolean;
    headerCtaNewTab?: boolean;
    ctaButtonVariant?: 'solid' | 'ghosted-light';
    showHeaderCta?: boolean;
    headerCtaIcon?: 'lock' | 'chevron' | 'back';

    signInCtaLabel?: string;

    container?: TCreateSideMenu;
    homeLabel?: string;
    homeLink?: string;

    forgotUserNameLink?: string;
    forgotPasswordLink?: string;
    setupAccountLink?: string;

    smsIcon?: Icon;
    emailIcon?: Icon;
    callIcon?: Icon;

    preloadDesktopImage?: boolean;

    removeHeader?: boolean;
    removeSignInBox?: boolean;
}

export const BillboardEditConfig = {
    emptyLabel: 'Billboard',
    isEmpty: (props: PropsWithChildren<IBillboardProps>) => {
        return !props || !props.mainText;
    },
};

const sendMarketingData = () => {
    const marketingData = getMarketingAttributes();
    // Developer's Note ::: DPM-8327
    //  /api/v1/marketingdata expects the data in {Key: string, Value: string} format
    //    whereas our acquisitionsapi expects the data in {Name: string, Value: string} format.
    if (Object.keys(marketingData).length > 0) {
        const reformattedMarketingData = Object.entries(marketingData).map(([Key, Value]) => ({ Key, Value }));
        axios.post('/api/v1/marketingdata', reformattedMarketingData)
            .then(() => {
                // eslint-disable-next-line no-console
                console.debug('Marketing Data API Set/Called');
            })
            .catch(() => {
                console.error('Error Has Occurred With Marketing Data');
            });
    }
};

const Billboard = (props: PropsWithChildren<IBillboardProps>): JSX.Element => {
    const { hamburgerOpen, setHamburgerOpen, hamburgerSide, setHamburgerContainer } = useContext(SideMenuContext);
    const { width } = useWidthContext();
    const { mainText = '', subText = '' } = props;
    const [isOpen, setOpen] = useState(false);
    const { className = '',
        tertiaryTextDesktop = '',
        tertiaryTextMobile = '',
        homeLabel = 'Home',
        homeLink = '/',
    } = props;
    const isMobile = width <= 768;
    const tertiaryText = isMobile ? tertiaryTextMobile : tertiaryTextDesktop;
    const imgPath = isMobile && props.mainMobileImgPath ? props.mainMobileImgPath : props.mainImgPath;
    const [ styleOverride, setStyleOverride ] = useState<React.CSSProperties>(undefined);
    const imageWidth = isMobile ? DEFAULT_MOBILE_IMAGE_WIDTH : DEFAULT_DESKTOP_IMAGE_WIDTH;
    const imageHeight = isMobile ? DEFAULT_MOBILE_IMAGE_HEIGHT : DEFAULT_DESKTOP_IMAGE_HEIGHT;

    const getNavBarLinks = useCallback((map: IMainNavigationItem[] | undefined) => {
        const navBarLinks: { label: any; path: string; param: string; newTab: boolean; }[] = [];
        if (map !== undefined) {
            map.forEach(item => {
                const obj = {
                    label: item.linkTitle,
                    path: item.link,
                    param: item.linkParam,
                    newTab: item.linkNewTab,
                };
                navBarLinks.push(obj);
            });
        }
        return navBarLinks;
    }, []);

    const getCTAParams = useCallback(() => {
        if (!props.headerCtaLabel) {
            return;
        }

        const obj = {
            label: props.headerCtaLabel,
            newTab: props.headerCtaNewTab,
            icon: props.headerCtaIcon,
            path: !props.headerCtaModal ? props.headerCtaLink : undefined,
            callback: props.headerCtaModal ? () => setOpen(!isOpen) : undefined,
        };
        return obj;

    }, []);

    useEffect(() => {
        if (props.author) {
            return;
        }

        const timeoutId = setTimeout(() => {
            sendMarketingData();
        }, 0);

        return () => {
            clearTimeout(timeoutId);
        };
    }, []);

    useEffect(() => {
        if (!props.usingOverwriteMobileStyle || !props.overwriteMobileStyle) {
            setStyleOverride(undefined);
            return;
        }

        try {
            setStyleOverride(JSON.parse(props.overwriteMobileStyle));
        } catch (exception) {
            console.error('Invalid JSON in Overwrite Mobile Style : ' + exception);
        }
    }, [props]);

    useEffect(() => {
        if (!props.container) {
            return;
        }

        const obj = {
            'title': transformHTMLParserLinks(homeLabel),
            'link': homeLink,
            'next': props.container,
        };

        if (!props.removeHeader) {
            setHamburgerContainer(obj);
        }
    }, [props.container, homeLabel, homeLink, setHamburgerContainer]);

    if(props.removeSignInBox) {
        return (<BillboardNoSignInBoxVariant {...props}/>);
    } else {
        return (
            <div id='billboard' className={`Billboard ${className}`}>
                {isMobile && (
                    <Modal isOpen={isOpen} modalOpen={setOpen}>
                        {retrieveSignInBox(
                            width,
                            props.signInCtaLabel,
                            props.signInTitle,
                            props.forgotPasswordLink,
                            props.forgotUserNameLink,
                            props.setupAccountLink,
                            props.smsIcon,
                            props.emailIcon,
                            props.callIcon,
                        )}
                    </Modal>
                )}
                {props.preLoadDesktopBkgTopImage && props.bkgTopImgPath &&
                    <img className='hidden' src={props.bkgTopImgPath} />
                }
                <Section className={`Billboard__section${props.removeHeader ? '--hiddenHeader': ''}`} containerClass='Billboard__container' bgImg={isMobile ? props.mobileBkgTopImgPath : props.bkgTopImgPath}>
                    {!props.removeHeader && <Header
                        className='Billboard__header'
                        theme='transparent'
                        navBarLinks={getNavBarLinks(props.navBarLinks)}
                        hideNavButtonsOnDesktop={props.hideCtaBtnDesktop}
                        showCTAButton={props.showHeaderCta}
                        CTAParams={getCTAParams()}
                        hamburger={props.showHamburgerButton}
                        hamburgerSide={hamburgerSide}
                        isHamburgerOpen={hamburgerOpen}
                        hamburgerCallBack={() => setHamburgerOpen(!hamburgerOpen)}
                    />}
                    <Row className='Billboard__row' align='start'>
                        <Col lg={5} className='Billboard__heroContainer COL-6'>
                            <Title data-testid='mainText'
                                align='left'
                                className='Billboard__mainText'>
                                <span>{transformHTMLParserLinks(mainText)}</span>
                            </Title>
                            <p className='Billboard__subText'>
                                {transformHTMLParserLinks(subText)}
                            </p>
                            {props.ctaLinkLabel && props.ctaLink && <Button className={`Billboard__heroButton ${props.ctaButtonVariant === 'solid' ? 'Billboard__heroButton--whiteOutlineVariant' : ''}`} size='large' variant={props.ctaButtonVariant} href={props.ctaLink} target={props.ctaNewTab ? '_blank' : '_self'}>
                                {transformHTMLParserLinks(props.ctaLinkLabel) as any}
                            </Button>}
                            {tertiaryText && <span className='Billboard__tertiaryText'>{transformHTMLParserLinks(tertiaryText)}</span>}
                        </Col>
                        <Col className='Billboard__imgContainer'>
                            {props.preloadDesktopImage && props.mainImgPath && <img fetchpriority='high' className='hidden' src={props.mainImgPath} />}
                            <img fetchpriority='high' className={`Billboard__mainImg${isMobile ? '--mobile' : '--desktop'} ${!(imgPath?.length > 0) ? 'opacity-0' : ''}`}
                                id={`Billboard__mainImg${isMobile ? '--mobile' : '--desktop'}`}
                                alt={props.mainImgAltText}
                                src={imgPath}
                                width={imageWidth}
                                height={imageHeight}
                                style={isMobile ? styleOverride : undefined }/>
                        </Col>
                        <Col className='Billboard__signInContainer'>
                            {!isMobile &&
                                retrieveSignInBox(
                                    width,
                                    props.signInCtaLabel,
                                    props.signInTitle,
                                    props.forgotPasswordLink,
                                    props.forgotUserNameLink,
                                    props.setupAccountLink,
                                    props.smsIcon,
                                    props.emailIcon,
                                    props.callIcon,
                                    props.removeHeader,
                                    props.usingOktaIdentityEngine
                            )}
                        </Col>
                    </Row>
                </Section>
            </div>
        );
    }
};
export default Billboard;
